import { Card } from "design-system/molecules/Card"
import { MetricCard } from "design-system/organisms/insights/MetricCard"
import { formatNumber, formatPercent, isNil } from "lopermut"
import { MoveRight } from "lucide-react"
import { useCampaignsImpactQuery } from "modules/campaigns/hooks/useCampaignsImpactQuery"
import {
  useCustomersMetricQuery,
  useInsightsCapturedMetricQuery,
} from "modules/dashboard/hooks/useMetricsQueries"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { CampaignImpactChart } from "./CampaignImpactChart"

export const CampaignsActivity = () => {
  const { t } = useTranslation()

  const { data: customers, isPending: isCustomersPending } = useCustomersMetricQuery()
  const { data: insightsCaptured, isPending: isInsightsPending } = useInsightsCapturedMetricQuery()

  const [activeMetricIdx, setActiveMetricIdx] = useState(0)
  const { data: campaignsImpactData, isPending: isCampaignsImpactPending } =
    useCampaignsImpactQuery()

  const engamentPercentage =
    customers?.impacted.total && customers?.impacted.total > 0
      ? ((customers?.engaged.total ?? 0) / customers?.impacted.total) * 100
      : null

  const conversionPercentage =
    customers?.engaged.total && customers?.engaged.total > 0
      ? ((customers?.achieved.total ?? 0) / customers?.engaged.total) * 100
      : null

  // Determine which dataKey to use based on active metric
  const getImpactDataKey = () => {
    if (activeMetricIdx === 0) return "reached"
    if (activeMetricIdx === 1) return "engaged"
    if (activeMetricIdx === 2) return "achieved"
    if (activeMetricIdx === 3) return "insights"

    return "reached"
  }

  return (
    <Card className="flex flex-col w-full min-w-xs p-6 gap-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4 w-full">
        <MetricCard
          key="impactedCustomers"
          title={t("campaigns.detail.activity.reachedCustomers.title")}
          tooltip={t("campaigns.detail.activity.reachedCustomers.description")}
          currentValue={formatNumber({
            amount: customers?.impacted.total,
            abvLevel: (customers?.impacted.total ?? 0) > 1000 ? 3 : 0,
          })}
          deltaPercentage={customers?.impacted.deltaPercentage}
          deltaTooltip={t("campaigns.detail.activity.deltaTooltip")}
          onClick={() => setActiveMetricIdx(0)}
          isActive={activeMetricIdx === 0}
          isPending={isCustomersPending}
        />
        <MetricCard
          key="engagedCustomers"
          title={t("campaigns.detail.activity.engagedCustomers.title")}
          tooltip={t("campaigns.detail.activity.engagedCustomers.description")}
          currentValue={formatNumber({
            amount: customers?.engaged.total,
            abvLevel: (customers?.engaged.total ?? 0) > 1000 ? 3 : 0,
          })}
          metric={
            !isNil(engamentPercentage) && (
              <div className="flex flex-row items-center gap-1">
                <MoveRight className="size-4" />
                {t("campaigns.detail.activity.engagedCustomers.percent", {
                  percent: formatPercent({
                    amount: engamentPercentage,
                    fix: 0,
                  }),
                })}
              </div>
            )
          }
          metricTooltip={t("campaigns.detail.activity.engagedCustomers.numberTooltip")}
          onClick={() => setActiveMetricIdx(1)}
          isActive={activeMetricIdx === 1}
          isPending={isCustomersPending}
        />
        <MetricCard
          key="customersAchieved"
          title={t("campaigns.detail.activity.customersAchieved.title")}
          tooltip={t("campaigns.detail.activity.customersAchieved.description")}
          currentValue={formatNumber({
            amount: customers?.achieved.total,
            abvLevel: (customers?.achieved.total ?? 0) > 1000 ? 3 : 0,
          })}
          metric={
            !isNil(conversionPercentage) && (
              <div className="flex flex-row items-center gap-1">
                <MoveRight className="size-4" />
                {t("campaigns.detail.activity.customersAchieved.percent", {
                  percent: formatPercent({
                    amount: conversionPercentage,
                    fix: 0,
                  }),
                })}
              </div>
            )
          }
          metricTooltip={t("campaigns.detail.activity.customersAchieved.numberTooltip")}
          onClick={() => setActiveMetricIdx(2)}
          isActive={activeMetricIdx === 2}
          isPending={isCustomersPending}
        />
        <MetricCard
          key="responsesCaptured"
          title={t("campaigns.detail.activity.responsesCaptured.title")}
          tooltip={t("campaigns.detail.activity.responsesCaptured.description")}
          currentValue={formatNumber({
            amount: insightsCaptured?.total,
            abvLevel: (insightsCaptured?.total ?? 0) > 1000 ? 3 : 0,
          })}
          deltaPercentage={insightsCaptured?.deltaPercentage}
          deltaTooltip={t("campaigns.detail.activity.deltaTooltip")}
          onClick={() => setActiveMetricIdx(3)}
          isActive={activeMetricIdx === 3}
          isPending={isInsightsPending}
        />
      </div>
      <CampaignImpactChart
        data={campaignsImpactData}
        isPending={isCampaignsImpactPending}
        dataKey={getImpactDataKey()}
      />
    </Card>
  )
}
